import React, { useEffect, useState } from "react";
import { adminApi } from "../../../api/adminApi";
import { Eye, EyeOff, AlertTriangle, Send, Users } from "lucide-react";
import { toast } from "react-toastify";
import StoreCompletionReminderModal from "./StoreCompletionReminderModal";
import StoreCompletionReminderLog from "./StoreCompletionReminderLog";
import "./EmailSettings.css";

const emptySettings = {
  email: {
    host: "",
    port: 587,
    secure: false,
    user: "",
    fromName: "",
    fromEmail: "",
    passwordConfigured: false,
    password: "",
  },
  notifications: {
    incompleteStoreRemindersEnabled: false,
    reminderAfterDays: 3,
    reminderRepeatDays: 7,
    supportSessionDurationMinutes: 30,
  },
};

// Merge an API response into a full settings object (password is never
// returned by the API, so it always starts empty).
const fromApi = (data = {}) => ({
  email: { ...emptySettings.email, ...data.email, password: "" },
  notifications: { ...emptySettings.notifications, ...data.notifications },
});

// Numbers are kept as typed while editing (so a field can be cleared) and
// converted only when sending.
const toPayload = (settings) => ({
  ...settings,
  email: { ...settings.email, port: Number(settings.email.port) },
  notifications: {
    ...settings.notifications,
    reminderAfterDays: Number(settings.notifications.reminderAfterDays),
    reminderRepeatDays: Number(settings.notifications.reminderRepeatDays),
    supportSessionDurationMinutes: Number(
      settings.notifications.supportSessionDurationMinutes,
    ),
  },
});

const Spinner = () => <span className="es-spinner" aria-hidden="true" />;

const Switch = ({ id, checked, onChange, label, description, warn }) => (
  <label className="es-switch" htmlFor={id}>
    <input
      id={id}
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={onChange}
      aria-describedby={description ? `${id}-desc` : undefined}
    />
    <span className="es-switch-track" aria-hidden="true" />
    <span className="es-switch-text">
      <span className="es-switch-label">{label}</span>
      {description && (
        <span
          id={`${id}-desc`}
          className={`es-switch-desc ${warn ? "is-warn" : ""}`}
        >
          {description}
        </span>
      )}
    </span>
  </label>
);

const EmailSettings = () => {
  const [settings, setSettings] = useState(emptySettings);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(emptySettings),
  );
  const [recipient, setRecipient] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderLogRefreshKey, setReminderLogRefreshKey] = useState(0);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await adminApi.getEmailSettings();
        if (response.success) {
          const next = fromApi(response.data);
          setSettings(next);
          setSavedSnapshot(JSON.stringify(next));
        }
      } catch (loadError) {
        setError(
          loadError.response?.data?.error || "Failed to load email settings.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const isDirty = JSON.stringify(settings) !== savedSnapshot;

  const updateEmail = (key, value) => {
    setSettings((current) => ({
      ...current,
      email: { ...current.email, [key]: value },
    }));
  };

  const updateNotifications = (key, value) => {
    setSettings((current) => ({
      ...current,
      notifications: { ...current.notifications, [key]: value },
    }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const response = await adminApi.saveEmailSettings(toPayload(settings));
      const next = fromApi(response.data);
      setSettings(next);
      setSavedSnapshot(JSON.stringify(next));
      toast.success("Settings saved.");
    } catch (saveError) {
      setError(saveError.response?.data?.error || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setError("");

    try {
      const response = await adminApi.sendTestEmail(recipient.trim());
      toast.success(response.message || "Test email sent.");
    } catch (testError) {
      setError(testError.response?.data?.error || "Failed to send test email.");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="email-settings-page" aria-busy="true" aria-live="polite">
        <div className="es-skeleton es-sk-title" />
        <div className="es-skeleton es-sk-card" />
        <div className="es-skeleton es-sk-card es-sk-short" />
        <span className="es-sr-only">Loading email settings</span>
      </div>
    );
  }

  const { email, notifications } = settings;
  const remindersOn = notifications.incompleteStoreRemindersEnabled;
  const port = Number(email.port);

  let tlsHint = "Leave off for port 587; the connection uses STARTTLS instead.";
  let tlsHintIsWarning = false;
  if (port === 465 && !email.secure) {
    tlsHint = "Port 465 normally needs implicit TLS. Turn this on.";
    tlsHintIsWarning = true;
  } else if (port === 587 && email.secure) {
    tlsHint = "Port 587 uses STARTTLS. Turn this off.";
    tlsHintIsWarning = true;
  }

  return (
    <div className="email-settings-page">
      <header className="es-header">
        <h1>Email &amp; notifications</h1>
        <p>
          Choose how the platform sends email and when clients get reminders.
        </p>
      </header>

      {error && (
        <div className="es-alert es-alert-error" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button
            type="button"
            className="es-link-btn"
            onClick={() => setError("")}
          >
            Dismiss
          </button>
        </div>
      )}

      <form className="es-form" onSubmit={saveSettings}>
        {/* ---------- Outgoing email ---------- */}
        <section
          className="es-card es-outgoing-card"
          aria-labelledby="es-outgoing-heading"
        >
          <div className="es-card-head">
            <h2 id="es-outgoing-heading">Outgoing email</h2>
            <p>SMTP details used for every email the platform sends.</p>
          </div>

          <div className="es-group es-server-group">
            <h3>Server</h3>
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="es-host">SMTP host</label>
                <input
                  id="es-host"
                  className="es-input"
                  value={email.host}
                  onChange={(e) => updateEmail("host", e.target.value)}
                  list="smtp-host-options"
                  autoComplete="off"
                  aria-describedby="es-host-hint"
                />
                <p id="es-host-hint" className="es-hint">
                  Pick a provider or type your own.
                </p>
              </div>
              <div className="es-field">
                <label htmlFor="es-port">SMTP port</label>
                <input
                  id="es-port"
                  className="es-input"
                  type="number"
                  min="1"
                  max="65535"
                  list="smtp-port-options"
                  value={email.port}
                  onChange={(e) => updateEmail("port", e.target.value)}
                  aria-describedby="es-port-hint"
                />
                <p id="es-port-hint" className="es-hint">
                  Usually 587 (STARTTLS) or 465 (implicit TLS).
                </p>
              </div>
            </div>

            <div className="es-switch-row">
              <Switch
                id="es-secure"
                checked={email.secure}
                onChange={(e) => updateEmail("secure", e.target.checked)}
                label="Use implicit TLS"
                description={tlsHint}
                warn={tlsHintIsWarning}
              />
            </div>
          </div>

          <div className="es-group es-signin-group">
            <h3>Sign-in</h3>
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="es-user">SMTP username</label>
                <input
                  id="es-user"
                  className="es-input"
                  value={email.user}
                  onChange={(e) => updateEmail("user", e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="es-field">
                <label htmlFor="es-password">
                  SMTP password
                  {email.passwordConfigured && !email.password && (
                    <span className="es-badge">Saved</span>
                  )}
                </label>
                <div className="es-input-wrap">
                  <input
                    id="es-password"
                    className="es-input es-input-with-action"
                    type={showPassword ? "text" : "password"}
                    value={email.password}
                    onChange={(e) => updateEmail("password", e.target.value)}
                    placeholder={
                      email.passwordConfigured
                        ? "Enter a new value to replace it"
                        : "Enter SMTP password"
                    }
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="es-input-action"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={
                      showPassword ? "Hide SMTP password" : "Show SMTP password"
                    }
                    title={
                      showPassword ? "Hide SMTP password" : "Show SMTP password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={18} aria-hidden="true" />
                    ) : (
                      <Eye size={18} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {email.passwordNeedsReset && (
              <div className="es-alert es-alert-error" role="alert">
                <AlertTriangle size={18} aria-hidden="true" />
                <span>
                  The saved SMTP password can&apos;t be decrypted with the
                  current production encryption key. Enter the password again
                  and save. Keep EMAIL_CONFIG_ENCRYPTION_KEY unchanged and
                  identical across all production app instances.
                </span>
              </div>
            )}
          </div>

          <div className="es-group es-sender-group">
            <h3>Sender</h3>
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="es-from-name">Sender name</label>
                <input
                  id="es-from-name"
                  className="es-input"
                  value={email.fromName}
                  onChange={(e) => updateEmail("fromName", e.target.value)}
                />
              </div>
              <div className="es-field">
                <label htmlFor="es-from-email">Sender email</label>
                <input
                  id="es-from-email"
                  className="es-input"
                  type="email"
                  value={email.fromEmail}
                  onChange={(e) => updateEmail("fromEmail", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="es-card-footer">
            <div className="es-field es-test-field">
              <label htmlFor="es-recipient">Send a test email</label>
              <input
                id="es-recipient"
                className="es-input"
                type="email"
                placeholder="name@example.com"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                aria-describedby="es-recipient-hint"
              />
              <p id="es-recipient-hint" className="es-hint">
                {isDirty
                  ? "You have unsaved changes. The test uses the last saved settings."
                  : "Check that your settings deliver before you rely on them."}
              </p>
            </div>
            <button
              type="button"
              className="es-btn"
              onClick={sendTestEmail}
              disabled={testing || !recipient.trim()}
            >
              {testing ? (
                <>
                  <Spinner /> Sending
                </>
              ) : (
                <>
                  <Send size={15} aria-hidden="true" /> Send test email
                </>
              )}
            </button>
          </div>
        </section>

        <datalist id="smtp-host-options">
          <option value="smtp.gmail.com">Gmail</option>
          <option value="smtp.office365.com">Microsoft 365 / Outlook</option>
          <option value="smtp.zoho.com">Zoho Mail</option>
          <option value="smtp.sendgrid.net">SendGrid</option>
          <option value="smtp.mailgun.org">Mailgun</option>
        </datalist>
        <datalist id="smtp-port-options">
          <option value="25" />
          <option value="465" />
          <option value="587" />
          <option value="2525" />
        </datalist>

        {/* ---------- Reminders ---------- */}
        <section className="es-card" aria-labelledby="es-reminders-heading">
          <div className="es-card-head es-card-head-row">
            <div>
              <h2 id="es-reminders-heading">Client reminder emails</h2>
              <p>
                Send store completion, payment, and website project reminders
                using separate email templates.
              </p>
            </div>
            <button
              type="button"
              className="es-btn"
              onClick={() => setShowReminderModal(true)}
            >
              <Users size={15} aria-hidden="true" /> Manage reminder emails
            </button>
          </div>

          <div className="es-group">
            <h3>Automatic reminders</h3>
            <div className="es-switch-row">
              <Switch
                id="es-reminders-enabled"
                checked={remindersOn}
                onChange={(e) =>
                  updateNotifications(
                    "incompleteStoreRemindersEnabled",
                    e.target.checked,
                  )
                }
                label="Email clients with unfinished stores"
                description="Sends a personalised reminder on the schedule below."
              />
            </div>

            <div className="es-grid" aria-disabled={!remindersOn}>
              <div className="es-field">
                <label htmlFor="es-after-days">
                  First reminder after (days)
                </label>
                <input
                  id="es-after-days"
                  className="es-input"
                  type="number"
                  min="1"
                  max="365"
                  value={notifications.reminderAfterDays}
                  onChange={(e) =>
                    updateNotifications("reminderAfterDays", e.target.value)
                  }
                  disabled={!remindersOn}
                />
              </div>
              <div className="es-field">
                <label htmlFor="es-repeat-days">Repeat after (days)</label>
                <input
                  id="es-repeat-days"
                  className="es-input"
                  type="number"
                  min="1"
                  max="365"
                  value={notifications.reminderRepeatDays}
                  onChange={(e) =>
                    updateNotifications("reminderRepeatDays", e.target.value)
                  }
                  disabled={!remindersOn}
                />
              </div>
            </div>
            {!remindersOn && (
              <p className="es-hint">
                Turn on automatic reminders to edit the schedule.
              </p>
            )}
          </div>

          <div className="es-group">
            <h3>Admin support sessions</h3>
            <div className="es-field es-support-duration">
              <label htmlFor="es-support-duration">
                Maximum client support session
              </label>
              <select
                id="es-support-duration"
                className="es-input"
                value={notifications.supportSessionDurationMinutes}
                onChange={(e) =>
                  updateNotifications(
                    "supportSessionDurationMinutes",
                    e.target.value,
                  )
                }
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
              </select>
              <p className="es-hint">
                Admins entering a client account will be identified in a banner.
                Their session ends automatically after this limit, and client
                changes are recorded under the admin in the audit log.
              </p>
            </div>
          </div>

          <p className="es-note">
            Automatic reminders currently apply to incomplete store setup.
            Payment and website reminders are sent manually to selected
            eligible requests.
          </p>
        </section>

        {/* ---------- Save bar ---------- */}
        <div className="es-savebar" role="region" aria-label="Save settings">
          <span
            className={`es-savebar-status ${isDirty ? "is-dirty" : ""}`}
            aria-live="polite"
          >
            {isDirty ? "You have unsaved changes" : "All changes saved"}
          </span>
          <button
            type="submit"
            className="es-btn es-btn-primary"
            disabled={saving || !isDirty}
          >
            {saving ? (
              <>
                <Spinner /> Saving
              </>
            ) : (
              "Save settings"
            )}
          </button>
        </div>
      </form>

      <StoreCompletionReminderLog refreshKey={reminderLogRefreshKey} />

      <StoreCompletionReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSent={() => setReminderLogRefreshKey((current) => current + 1)}
      />
    </div>
  );
};

export default EmailSettings;
